import { useState, useEffect, useCallback, useRef } from 'react';
import {
    DialogRoot,
    DialogContent,
    DialogHeader,
    DialogBody,
    DialogTitle,
    DialogCloseTrigger,
} from '@/components/ui/dialog';
import {
    Text,
    Stack,
    Button,
    Input,
    Box,
    Flex,
    Card,
    Spinner,
    EmptyState,
    Icon,
    Badge,
} from '@chakra-ui/react';
import { MagnifyingGlassIcon, DocumentIcon, SparklesIcon, CloudArrowUpIcon, ServerStackIcon } from '@heroicons/react/24/outline';
import api from '@/utils/api';
import { toaster } from '@/components/ui/toaster';
import useWorkspace from '@/lib/hooks/use-workspace';
import getSportName from '@/utils/get-sport-name';

interface GeneratedContent {
    name: string;
    description: string;
}

const MAX_CONTENT_NAMES = 50; // Maximum number of content names to show/generate

interface ContentSearchModalProps {
    isOpen: boolean;
    onClose: () => void;
    workspaceId: string;
    contentTypeId: string;
    contentTypeName: string;
    onSelect: (content: { name: string; description: string }) => void;
}

export default function ContentSearchModal({
    isOpen,
    onClose,
    workspaceId,
    contentTypeId,
    contentTypeName,
    onSelect,
}: ContentSearchModalProps) {
    const { workspace } = useWorkspace();
    const [searchQuery, setSearchQuery] = useState('');
    const [results, setResults] = useState<GeneratedContent[]>([]);
    const [filteredResults, setFilteredResults] = useState<GeneratedContent[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [knowledgeBaseError, setKnowledgeBaseError] = useState<string | null>(null);
    const [isFromDatabase, setIsFromDatabase] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const isLoadingRef = useRef(false);
    const hasLoadedRef = useRef(false);
    const lastLoadedKeyRef = useRef<string>('');
    const generateNamesRef = useRef<((forceRegenerate: boolean) => Promise<void>) | null>(null);

    // Fetch existing content names from database
    const fetchExistingNames = useCallback(async (): Promise<GeneratedContent[]> => {
        if (!workspaceId || !contentTypeId) {
            console.warn('⚠️ Cannot fetch: missing workspaceId or contentTypeId');
            return [];
        }

        console.log(`📡 Fetching from database: workspaceId=${workspaceId}, contentTypeId=${contentTypeId}`);

        try {
            const response = await api.get(`/content-names/workspace/${workspaceId}/${contentTypeId}`);
            console.log('📦 Raw API response:', response);
            console.log('📦 Response data:', response.data);
            console.log('📦 Response data type:', typeof response.data);
            console.log('📦 Is array?', Array.isArray(response.data));

            // Match the pattern from list-documents.tsx: response?.data?.data || []
            let data = response?.data;

            console.log('📦 Raw response.data:', data);
            console.log('📦 response.data type:', typeof data);
            console.log('📦 response.data.data:', data?.data);
            console.log('📦 response.data.data isArray?', Array.isArray(data?.data));

            // Handle different response formats
            // Pattern 1: Direct array response
            if (Array.isArray(data)) {
                console.log('✓ Response is directly an array, length:', data.length);
                // Use it directly
            }
            // Pattern 2: {data: [...]} or {data: null} - most common based on list-documents.tsx
            else if (data && typeof data === 'object') {
                // Check data.data first (this is what list-documents.tsx uses)
                if (Array.isArray(data.data)) {
                    console.log('✓ Found data.data array, length:', data.data.length);
                    data = data.data;
                }
                // Check if data.data is explicitly null (empty result)
                else if (data.data === null) {
                    console.log('ℹ️ data.data is null - no content names in database');
                    data = [];
                }
                // Pattern 3: Check other common formats
                else if (Array.isArray(data.results)) {
                    console.log('✓ Found data.results array, length:', data.results.length);
                    data = data.results;
                }
                else if (Array.isArray(data.content)) {
                    console.log('✓ Found data.content array, length:', data.content.length);
                    data = data.content;
                }
                // Pattern 4: Fallback - check any array property
                else {
                    const arrayKey = Object.keys(data).find(key => Array.isArray(data[key]));
                    if (arrayKey) {
                        console.log(`✓ Found array in property '${arrayKey}', length:`, data[arrayKey].length);
                        data = data[arrayKey];
                    } else {
                        console.warn('⚠️ API response object contains no array');
                        console.warn('⚠️ Object structure:', JSON.stringify(data, null, 2));
                        data = [];
                    }
                }
            }
            // Pattern 5: Null/undefined response
            else {
                console.log('ℹ️ Response data is null/undefined/other type:', data);
                data = [];
            }

            const contentNames = Array.isArray(data) ? data.map((item: any) => ({
                name: item.name || '',
                description: item.description || '',
            })).filter(item => item.name) : []; // Filter out items without names

            console.log(`✓ Parsed ${contentNames.length} content names from database:`, contentNames);
            if (contentNames.length > 0) {
                console.log('✓ SUCCESS: Found content names in database!');
            } else {
                console.log('ℹ️ No valid content names found in database response');
            }
            return contentNames;
        } catch (err: any) {
            console.error('❌ Error fetching existing content names from database:', err);
            console.error('❌ Error details:', {
                status: err.response?.status,
                statusText: err.response?.statusText,
                data: err.response?.data,
                message: err.message,
                url: err.config?.url,
            });

            // If it's a 404, that's fine - no content names exist yet
            if (err.response?.status === 404) {
                console.log('ℹ️ No existing content names found in database (404 - expected)');
            } else {
                // For other errors, log but don't fail silently
                console.warn('⚠️ Unexpected error fetching content names, will generate new ones');
            }
            return [];
        }
    }, [workspaceId, contentTypeId]);

    // Delete all existing content names for this workspace/content type
    const deleteAllContentNames = useCallback(async () => {
        if (!workspaceId || !contentTypeId) return;

        try {
            await api.delete(`/content-names/workspace/${workspaceId}/${contentTypeId}/all`);
            console.log('✓ Deleted all existing content names from database');
        } catch (err: any) {
            // If it's a 404, that's fine - nothing to delete
            if (err.response?.status !== 404) {
                console.error('Failed to delete existing content names:', err);
            }
        }
    }, [workspaceId, contentTypeId]);

    // Save generated names to database
    const saveGeneratedNames = useCallback(async (names: GeneratedContent[], shouldDeleteFirst: boolean = false) => {
        if (!workspaceId || !contentTypeId || names.length === 0) return;

        setIsSaving(true);
        try {
            // Delete existing records first if requested (e.g., on regenerate)
            if (shouldDeleteFirst) {
                console.log('Deleting existing content names before saving new ones...');
                await deleteAllContentNames();
            }

            await api.post(`/content-names/workspace/${workspaceId}/${contentTypeId}/batch`, {
                content_names: names.map(item => ({
                    name: item.name,
                    description: item.description || '',
                })),
            });
            console.log('Successfully saved generated names to database');
        } catch (err: any) {
            console.error('Failed to save generated names to database:', err);
            // Don't show error toast - just log it, the names are still displayed
        } finally {
            setIsSaving(false);
        }
    }, [workspaceId, contentTypeId, deleteAllContentNames]);

    // Generate names from knowledge base using AI
    const generateNames = useCallback(async (forceRegenerate: boolean = false) => {
        if (!workspaceId || !contentTypeId) {
            console.warn('Missing workspaceId or contentTypeId:', { workspaceId, contentTypeId });
            return;
        }

        // Prevent multiple simultaneous loads
        if (isLoadingRef.current && !forceRegenerate) {
            console.log('Already loading, skipping duplicate request');
            return;
        }

        isLoadingRef.current = true;
        setIsLoading(true);
        setHasLoaded(true);
        setKnowledgeBaseError(null);

        try {
            // ALWAYS check database first (unless forcing regenerate)
            if (!forceRegenerate) {
                console.log('STEP 1: Checking for existing content names in database...');
                try {
                    const existingNames = await fetchExistingNames();
                    console.log('STEP 1 RESULT: Found existing names:', existingNames.length);

                    if (existingNames.length > 0) {
                        console.log('✓ Using existing content names from database');
                        setResults(existingNames);
                        setFilteredResults(existingNames);
                        setIsFromDatabase(true);
                        hasLoadedRef.current = true;
                        setIsLoading(false);
                        isLoadingRef.current = false;
                        return; // Stop here - we have names from database
                    } else {
                        console.log('STEP 1 RESULT: No existing content names found in database, proceeding to generate');
                    }
                } catch (dbError: any) {
                    // If database check fails with a non-404 error, log it but continue to generate
                    console.error('Database check failed, but continuing to generate:', dbError);
                    if (dbError.response?.status !== 404) {
                        console.warn('Non-404 error from database, will still try to generate');
                    }
                }
            } else {
                console.log('Force regenerate requested, skipping database check');
            }

            // STEP 2: Only generate if database had no results (or force regenerate)
            console.log('STEP 2: Generating new names using Gemini...');
            setIsFromDatabase(false);
            const response = await api.post(
                `/knowledge-base/gemini/${workspaceId}/generate-names`,
                {
                    content_type_id: parseInt(contentTypeId),
                    count: MAX_CONTENT_NAMES,
                    country: workspace?.country || '',
                    sport_type: getSportName(workspace?.sport_type_id || 0),
                }
            );
            const generatedResults = response.data.results || [];
            console.log('STEP 2 RESULT: Generated', generatedResults.length, 'names');

            setResults(generatedResults);
            setFilteredResults(generatedResults);

            // STEP 3: Save generated names to database
            if (generatedResults.length > 0) {
                console.log('STEP 3: Saving generated names to database...');
                // If forceRegenerate, delete existing records first to replace them
                await saveGeneratedNames(generatedResults, forceRegenerate);
                setIsFromDatabase(true);
                console.log('✓ Saved generated names to database');
            }
            hasLoadedRef.current = true;
        } catch (err: any) {
            console.error('Failed to generate names from knowledge base:', err);
            const errorMessage = err.response?.data?.error || '';

            // Check if it's a "knowledge base not found" error
            if (errorMessage.includes('file search store not found') || errorMessage.includes('record not found')) {
                setKnowledgeBaseError('No knowledge base has been set up for this workspace yet. Please upload documents to your knowledge base first.');
            } else {
                toaster.create({
                    title: errorMessage || 'Failed to generate suggestions from knowledge base',
                    type: 'error',
                });
            }
            setResults([]);
            setFilteredResults([]);
        } finally {
            setIsLoading(false);
            isLoadingRef.current = false;
        }
    }, [workspaceId, contentTypeId, fetchExistingNames, saveGeneratedNames]);

    // Keep ref updated with latest generateNames function
    useEffect(() => {
        generateNamesRef.current = generateNames;
    }, [generateNames]);

    // Handle regenerate button click
    const handleRegenerate = useCallback(() => {
        generateNames(true); // Force regenerate
    }, [generateNames]);

    // Load names when modal opens
    useEffect(() => {
        if (isOpen && workspaceId && contentTypeId && generateNamesRef.current) {
            // ALWAYS check database first when modal opens (unless already loading)
            const key = `${workspaceId}-${contentTypeId}`;

            // Reset loaded state if workspace/contentType changed
            if (lastLoadedKeyRef.current !== key) {
                hasLoadedRef.current = false;
                lastLoadedKeyRef.current = key;
            }

            // Always try to load if not currently loading
            // This ensures we ALWAYS check the database every time the modal opens
            if (!isLoadingRef.current) {
                console.log('🔍 Modal opened - ALWAYS checking database first...');
                hasLoadedRef.current = false; // Reset to ensure we check database
                generateNamesRef.current(false); // false = check database first, don't force regenerate
            } else {
                console.log('⏳ Already loading, skipping duplicate request...');
            }
        }
    }, [isOpen, workspaceId, contentTypeId]); // Removed generateNames from deps, using ref instead

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSearchQuery('');
            setResults([]);
            setFilteredResults([]);
            setHasLoaded(false);
            setKnowledgeBaseError(null);
            setIsFromDatabase(false);
            isLoadingRef.current = false;
            hasLoadedRef.current = false;
        }
    }, [isOpen]);

    // Filter results based on search query
    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredResults(results);
        } else {
            const query = searchQuery.toLowerCase();
            const filtered = results.filter(
                (item) =>
                    item.name.toLowerCase().includes(query) ||
                    item.description.toLowerCase().includes(query)
            );
            setFilteredResults(filtered);
        }
    }, [searchQuery, results]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            // Filter is already applied via useEffect
        }
    };

    const handleSelectContent = (content: GeneratedContent) => {
        onSelect({
            name: content.name,
            description: content.description || '',
        });
    };

    const contentTypeLabel = contentTypeName.toLowerCase();

    return (
        <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && onClose()} size="xl">
            <DialogContent maxW="700px">
                <DialogHeader>
                    <DialogTitle>
                        <Flex align="center" gap={2}>
                            <Icon as={SparklesIcon} w={5} h={5} color="brand.500" />
                            AI-Generated {contentTypeName} Suggestions
                        </Flex>
                    </DialogTitle>
                    <DialogCloseTrigger />
                </DialogHeader>

                <DialogBody pb={6}>
                    <Stack gap={4}>
                        <Text color="gray.600" fontSize="sm">
                            Select a {contentTypeLabel} suggestion generated from your knowledge base to auto-fill the name and description.
                        </Text>

                        <Flex gap={2}>
                            <Input
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={`Filter ${contentTypeLabel} suggestions...`}
                                flex={1}
                            />
                            <Button
                                colorPalette="brand"
                                variant="outline"
                                onClick={handleRegenerate}
                                loading={isLoading || isSaving}
                            >
                                <Icon as={SparklesIcon} w={4} h={4} />
                                Regenerate
                            </Button>
                        </Flex>

                        {/* Source indicator */}
                        {!isLoading && results.length > 0 && (
                            <Flex justify="flex-end" mb={2}>
                                <Badge
                                    colorPalette={isFromDatabase ? 'blue' : 'green'}
                                    variant="subtle"
                                    size="sm"
                                >
                                    <Flex align="center" gap={1}>
                                        <Icon as={isFromDatabase ? ServerStackIcon : SparklesIcon} w={3} h={3} />
                                        {isFromDatabase ? 'From saved suggestions' : 'Freshly generated'}
                                    </Flex>
                                </Badge>
                            </Flex>
                        )}

                        <Box maxH="400px" overflowY="auto">
                            {isLoading ? (
                                <Flex justify="center" align="center" py={8} direction="column" gap={3}>
                                    <Spinner size="lg" colorPalette="brand" />
                                    <Text color="gray.500" fontSize="sm">
                                        {isSaving
                                            ? `Saving ${contentTypeLabel} suggestions...`
                                            : `Loading ${contentTypeLabel} suggestions...`
                                        }
                                    </Text>
                                </Flex>
                            ) : knowledgeBaseError ? (
                                <EmptyState.Root>
                                    <EmptyState.Content>
                                        <EmptyState.Indicator>
                                            <Icon as={CloudArrowUpIcon} w={8} h={8} />
                                        </EmptyState.Indicator>
                                        <EmptyState.Title>Knowledge Base Not Set Up</EmptyState.Title>
                                        <EmptyState.Description>
                                            {knowledgeBaseError}
                                        </EmptyState.Description>
                                    </EmptyState.Content>
                                </EmptyState.Root>
                            ) : filteredResults.length > 0 ? (
                                <Stack gap={2}>
                                    {filteredResults.map((content, index) => (
                                        <Card.Root
                                            key={`${content.name}-${index}`}
                                            variant="outline"
                                            cursor="pointer"
                                            _hover={{ bg: 'gray.50', borderColor: 'brand.500' }}
                                            onClick={() => handleSelectContent(content)}
                                        >
                                            <Card.Body p={4}>
                                                <Flex justify="space-between" align="flex-start" gap={3}>
                                                    <Flex gap={3} flex={1}>
                                                        <Icon as={SparklesIcon} w={5} h={5} color="brand.500" mt={0.5} flexShrink={0} />
                                                        <Box flex={1}>
                                                            <Text fontWeight="medium" color="gray.700" mb={1}>
                                                                {content.name}
                                                            </Text>
                                                            {content.description && (
                                                                <Text
                                                                    fontSize="sm"
                                                                    color="gray.500"
                                                                    overflow="hidden"
                                                                    textOverflow="ellipsis"
                                                                    css={{
                                                                        display: '-webkit-box',
                                                                        WebkitLineClamp: 2,
                                                                        WebkitBoxOrient: 'vertical',
                                                                    }}
                                                                >
                                                                    {content.description}
                                                                </Text>
                                                            )}
                                                        </Box>
                                                    </Flex>
                                                    <Button variant="ghost" size="sm" colorPalette="brand">
                                                        Select
                                                    </Button>
                                                </Flex>
                                            </Card.Body>
                                        </Card.Root>
                                    ))}
                                </Stack>
                            ) : hasLoaded && results.length > 0 && searchQuery.trim() ? (
                                <EmptyState.Root>
                                    <EmptyState.Content>
                                        <EmptyState.Indicator>
                                            <Icon as={MagnifyingGlassIcon} w={8} h={8} />
                                        </EmptyState.Indicator>
                                        <EmptyState.Title>No matching suggestions</EmptyState.Title>
                                        <EmptyState.Description>
                                            Try a different filter term or clear the filter to see all suggestions.
                                        </EmptyState.Description>
                                    </EmptyState.Content>
                                </EmptyState.Root>
                            ) : hasLoaded ? (
                                <EmptyState.Root>
                                    <EmptyState.Content>
                                        <EmptyState.Indicator>
                                            <Icon as={DocumentIcon} w={8} h={8} />
                                        </EmptyState.Indicator>
                                        <EmptyState.Title>No suggestions found</EmptyState.Title>
                                        <EmptyState.Description>
                                            Upload documents to your knowledge base to generate {contentTypeLabel} suggestions.
                                        </EmptyState.Description>
                                    </EmptyState.Content>
                                </EmptyState.Root>
                            ) : (
                                <Box py={8} textAlign="center">
                                    <Icon as={SparklesIcon} w={12} h={12} color="gray.300" mb={4} />
                                    <Text color="gray.500">
                                        Loading {contentTypeLabel} suggestions...
                                    </Text>
                                </Box>
                            )}
                        </Box>
                    </Stack>
                </DialogBody>
            </DialogContent>
        </DialogRoot>
    );
}
